import {
  ColorModeProvider,
  Dialog,
  DialogContent,
  DialogHeader,
  MenuItem,
  Select,
  Typography,
} from '@airbus/components-react';
import { Box } from '@mui/material';
import styles from './ProfileDialog.module.css';
import { useLanguageContextAPI } from '../../shared/context/LanguageContext';
import { ChangeEvent, useEffect, useState } from 'react';
import { Lang } from '../../shared/types';
import { useUserContextApi } from '../../shared/context/UserContext';
import { useAlertBar } from '../../shared/context/AlertBarContext';
import { CONFIG_SCHEMA } from '../../shared/services/config';
import { getApiBaseUrl } from '../../shared/utils/apiBase';
import ProfitCenterSelection from './ProfitCenterSelection';

const ProfileDialog = (props) => {
  const { open, setOpen, roleNames } = props;
  const { t, changeLang } = useLanguageContextAPI();
  const { loginDetails, userDetails } = useUserContextApi();
  const { setAlertbar } = useAlertBar();
  const apiBase = getApiBaseUrl();
  const userAccountId = loginDetails?.userId;
  const userId = userDetails?.userId;

  const languageOptions: { label: string; value: Lang | 'no_language' }[] = [
    { label: 'Browser Default', value: 'no_language' },
    { label: 'English', value: 'en' },
    { label: 'German', value: 'de' },
  ];

  const [globalLanguage, setGlobalLanguage] = useState<Lang | 'no_language'>();
  useEffect(() => {
    // Check Profile Language Preference
    async function fetchFn() {
      const response = await fetch(
        `${apiBase}/api/getProfileLanguage?` + new URLSearchParams({ userAccountId }),
      );
      const data = await response.json();
      const fetchedLanguage = data?.[`${CONFIG_SCHEMA}_mbd_users`]?.[0]?.language;
      changeLang(fetchedLanguage);
      setGlobalLanguage(fetchedLanguage);
    }
    fetchFn();
  }, []);

  // Change Default Language Profile Preference
  async function onChangeHandler(event: ChangeEvent<HTMLSelectElement>) {
    const language = event.target.value as Lang | 'no_language';
    const set = { language: language === 'no_language' ? null : language };
    const response = await fetch(`${apiBase}/api/userSession/${userAccountId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ set }),
    });
    if (!response.ok) setAlertbar({ children: t('error.profile_language'), severity: 'error' });
    else {
      setGlobalLanguage(language);
      if (language !== 'no_language') {
        changeLang(language);
      }
      const jsonResult = await response.json();
      if (await jsonResult?.errors)
        setAlertbar({ children: t('error.profile_language'), severity: 'error' });
    }
  }

  return (
    <>
      <ColorModeProvider mode='light'>
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          aria-labelledby='modal-modal-title'
          aria-describedby='modal-modal-description'
        >
          <DialogHeader>
            <Typography variant='h6'>{t('profile.profile')}</Typography>
          </DialogHeader>
          <DialogContent>
            <div className={styles.contentSection}>
              <div>
                <Typography variant='medium' style={{ marginBottom: '0.5rem' }}>
                  {t('profile.default_setting')}
                </Typography>
                <Select
                  aria-label='Select an option'
                  value={globalLanguage ? globalLanguage : 'no_language'}
                  options={languageOptions}
                  onChange={onChangeHandler}
                ></Select>
              </div>
              <div>
                <Typography variant='medium' style={{ marginBottom: '0.5rem' }}>
                  {t('profile.assigned_roles')}{' '}
                </Typography>
                {roleNames && (
                  <MenuItem
                    key={'role'}
                    style={{
                      marginTop: '0.25rem',
                      backgroundColor: 'transparent',
                      cursor: 'default',
                      padding: 0,
                    }}
                  >
                    <div
                      style={{ maxHeight: '20rem', overflowY: 'auto', marginBottom: '2rem' }}
                      className={styles.innerViewBox}
                    >
                      <Box display='grid' gap={1.5} style={{ margin: '10px' }}>
                        {roleNames.map((roleObj) => {
                          return (
                            <>
                              <div
                                style={{ fontWeight: 'bold', display: 'contents' }}
                                className={styles.viewlist}
                              >
                                {roleObj.role_name}
                              </div>{' '}
                              - {roleObj.description}
                              <br />
                            </>
                          );
                        })}
                      </Box>
                    </div>
                  </MenuItem>
                )}
              </div>
            </div>
            <ProfitCenterSelection open={open} userId={userId} />
          </DialogContent>
        </Dialog>
      </ColorModeProvider>
    </>
  );
};

export default ProfileDialog;
